import {
  type Bloque,
  type Contenido,
  nuevoIdDeBloque,
  type Problema,
  type Seccion,
  textoDe,
} from "./contenido.js";

/**
 * Las plantillas por tipo de documento: qué secciones trae cada uno, con qué títulos y cuáles
 * son obligatorias. El editor arranca una revisión con ellas (`contenidoVacio`), la barra de
 * estado cuenta las que faltan (`seccionesFaltantes`) y la API comprueba que nadie se salga del
 * formato (`problemasDePlantilla`).
 */

export type SeccionDePlantilla = {
  clave: string;
  titulo: string;
  obligatoria: boolean;
  /** La ayuda que muestra el editor al pararse en la sección. */
  ayuda: string;
  /**
   * La llena el sistema, no la persona: "Control de las actualizaciones" sale del historial de
   * revisiones. En el editor se ve pero no se edita; su `content` va vacío.
   */
  automatica?: boolean;
};

/**
 * Las diez secciones de los documentos reales de SMG, en su orden y con sus títulos, copiadas
 * de GES-PRG-009 Rev. 01 (Programa de control de químicos, marzo 2025), el primer documento
 * real que entregó Calidad el 2026-09-22. Reemplazan a las siete del mockup, que eran inventadas.
 */
const ISO_SMG: SeccionDePlantilla[] = [
  {
    clave: "objetivo",
    titulo: "Objetivo",
    obligatoria: true,
    ayuda: "Para qué existe este documento, en dos o tres líneas.",
  },
  {
    clave: "alcance",
    titulo: "Alcance",
    obligatoria: true,
    ayuda: "A qué procesos, áreas o productos aplica, y a cuáles no.",
  },
  {
    clave: "definiciones",
    titulo: "Definiciones",
    obligatoria: false,
    ayuda: "Términos y siglas que alguien nuevo no entendería solo. El término va en negrita.",
  },
  {
    clave: "responsabilidad",
    titulo: "Responsabilidad",
    obligatoria: true,
    ayuda: "«Es responsabilidad del [puesto]:» y debajo, en viñetas, qué le toca.",
  },
  {
    clave: "desarrollo",
    titulo: "Descripción de actividades",
    obligatoria: true,
    ayuda:
      "Numera las actividades como subtítulos (5.1, 5.2…). Cada paso puede indicar quién lo ejecuta.",
  },
  {
    clave: "registros",
    titulo: "Registros",
    obligatoria: false,
    ayuda:
      "Los registros que este documento genera, con su código: Registro de derrames (GES-REG-019).",
  },
  {
    clave: "relacionados",
    titulo: "Documentos relacionados",
    obligatoria: false,
    ayuda: "Otros documentos del sistema que este cita, con su código.",
  },
  {
    clave: "referencia",
    titulo: "Documentos de referencia",
    obligatoria: false,
    ayuda: "Normas, reglamentos y acuerdos externos en los que se basa.",
  },
  {
    clave: "actualizaciones",
    titulo: "Control de las actualizaciones",
    obligatoria: false,
    ayuda: "La llena el sistema con el historial de revisiones.",
    automatica: true,
  },
  {
    clave: "anexos",
    titulo: "Anexos",
    obligatoria: false,
    ayuda: "Formatos, tablas de referencia o imágenes que acompañan al documento.",
  },
];

/**
 * Qué secciones trae cada tipo. Instructivo, procedimiento y programa comparten el esqueleto de
 * SMG. Un tipo sin plantilla (`EXT`, y lo que viene de fuera: matrices, planos, especificaciones)
 * no se redacta: se sube.
 */
export const PLANTILLAS_POR_TIPO: Record<string, SeccionDePlantilla[]> = {
  INS: ISO_SMG,
  PRO: ISO_SMG,
  PRG: ISO_SMG,
  REG: [
    {
      clave: "desarrollo",
      titulo: "Contenido del registro",
      obligatoria: true,
      ayuda: "El formato que se llena: normalmente una tabla.",
    },
  ],
  /** Fase 3b: un memorándum o comunicado. Una sola sección, sin las diez de SMG. */
  INF: [
    {
      clave: "contenido",
      titulo: "Contenido",
      obligatoria: true,
      ayuda: "Lo que se comunica. Se publica sin ruta de firmas.",
    },
  ],
};

export function plantillaDe(tipoCodigo: string): SeccionDePlantilla[] | null {
  return PLANTILLAS_POR_TIPO[tipoCodigo] ?? null;
}

/** ¿Este tipo se redacta en el editor, o solo se sube como archivo? */
export function seRedacta(tipoCodigo: string): boolean {
  return plantillaDe(tipoCodigo) !== null;
}

/** Un párrafo vacío con su id: lo mínimo que TipTap acepta dentro de una sección. */
function parrafoVacio(): Bloque {
  return { type: "paragraph", attrs: { id: nuevoIdDeBloque() } };
}

/** El documento con el que arranca una revisión nueva: las secciones de la plantilla, vacías. */
export function contenidoVacio(tipoCodigo: string): Contenido {
  const plantilla = plantillaDe(tipoCodigo);
  if (!plantilla) throw new RangeError(`El tipo ${tipoCodigo} no se redacta en el editor`);
  return {
    type: "doc",
    content: plantilla.map((s) => ({
      type: "seccion",
      attrs: { id: nuevoIdDeBloque(), titulo: s.titulo, clave: s.clave },
      // Una sección automática nace y se queda vacía: la hoja la llena con el historial.
      content: s.automatica ? [] : [parrafoVacio()],
    })),
  };
}

/** ¿Esta sección la llena el sistema? (No se edita ni cuenta como faltante.) */
export function esSeccionAutomatica(tipoCodigo: string, clave: string | null): boolean {
  return !!plantillaDe(tipoCodigo)?.find((s) => s.clave === clave)?.automatica;
}

/**
 * Lo que el esquema no ve: que las secciones sean **exactamente** las de la plantilla del tipo,
 * en su orden y con sus títulos, y que las automáticas vengan vacías. El editor no deja salirse
 * de la plantilla; esto atrapa a quien le hable a la API por fuera de él. El formato de SMG es
 * fijo (diez secciones, siempre las mismas), así que ni una de más ni una de menos.
 */
export function problemasDePlantilla(contenido: Contenido, tipoCodigo: string): Problema[] {
  const plantilla = plantillaDe(tipoCodigo);
  if (!plantilla)
    return [{ ruta: "$", mensaje: `El tipo ${tipoCodigo} no se redacta en el editor` }];
  const problemas: Problema[] = [];
  if (contenido.content.length !== plantilla.length) {
    problemas.push({
      ruta: "$.content",
      mensaje: `La plantilla tiene ${plantilla.length} secciones y llegaron ${contenido.content.length}`,
    });
  }
  plantilla.forEach((p, i) => {
    const s = contenido.content[i];
    if (!s) return;
    if (s.attrs.clave !== p.clave || s.attrs.titulo !== p.titulo) {
      problemas.push({
        ruta: `$.content.${i}`,
        mensaje: `La sección ${i + 1} tiene que ser "${p.titulo}"`,
      });
    } else if (p.automatica && s.content.length > 0) {
      problemas.push({
        ruta: `$.content.${i}`,
        mensaje: `"${p.titulo}" la llena el sistema: no se edita`,
      });
    }
  });
  return problemas;
}

/**
 * Las secciones obligatorias de la plantilla que faltan o están vacías. Es lo que la barra de
 * estado cuenta ("Faltan 2 secciones") y lo que bloquea *Enviar a revisión*.
 */
export function seccionesFaltantes(contenido: Contenido, tipoCodigo: string): SeccionDePlantilla[] {
  const plantilla = plantillaDe(tipoCodigo) ?? [];
  return plantilla.filter((p) => {
    if (!p.obligatoria || p.automatica) return false;
    const s = contenido.content.find((x) => x.attrs.clave === p.clave);
    return !s || !seccionTieneContenido(s);
  });
}

/** ¿La sección dice algo? Un párrafo vacío, o solo espacios, no cuenta. */
function seccionTieneContenido(s: Seccion): boolean {
  return s.content.some((b) => {
    if (b.type === "imagen" || b.type === "flujograma" || b.type === "listaDeVerificacion") {
      return true;
    }
    return textoDe(b).trim().length > 0;
  });
}
